import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Container, Table, Input } from 'reactstrap';
import { useBoolean } from 'usehooks-ts';
import { debounce } from 'lodash';

import Breadcrumb from 'components/App/Breadcrumb';
import Button from 'components/App/Button';
import Meta from 'components/App/Meta';
import ModalAddAccommodationAccount from 'components/Modals/ModalAddAccommodationAccount';
import ModalAdminConfirm from 'components/Modals/ModalAdminConfirm';
import ModalUpgradeCustomerAccount from 'components/Modals/ModalUpgradeCustomerAccount';
import Pagination from 'components/Pagination';

import AddIcon from 'assets/images/add-white.svg';
import SearchIcon from 'assets/images/search-icon.svg';
import images from 'assets/images';
import { showSuccess } from 'utils';
import {
  normalizeCustomerFromApi,
  getCustomerList,
  blockCustomer,
  unblockCustomer,
} from './ManagerCustomerAccount.container';

import styles from './ManagerCustomerAccount.module.scss';

const PAGE_TITLE = 'Tài khoản khách hàng';

const breadcrumbList = [
  {
    name: 'Quản lý tài khoản',
    isActive: false,
    link: '',
  },
  {
    name: PAGE_TITLE,
    isActive: true,
    link: '',
  },
];

const ITEMS_PER_PAGE = 10;

const TABS = {
  ACTIVE: 'ACTIVE',
  BLOCKED: 'BLOCKED',
};

const S3_AVATAR_PREFIX = 'https://dashment-dev.s3.ap-southeast-1.amazonaws.com';

function ManagerCustomerAccount() {
  const [customers, setCustomers] = useState([]);
  const [activeTab, setActiveTab] = useState(TABS.ACTIVE);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [debouncedSearchKeyword, setDebouncedSearchKeyword] = useState('');
  const [blockingUser, setBlockingUser] = useState(null);
  const [upgradingUser, setUpgradingUser] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const {
    value: isOpenModalAddAccount,
    setTrue: onOpenModalAddAccount,
    toggle: onToggleModalAddAccount,
  } = useBoolean(false);

  useEffect(() => {
    const payload = {
      isBlocked: activeTab === TABS.BLOCKED,
    };
    const keyword = debouncedSearchKeyword.trim();
    if (keyword) {
      payload.q = keyword;
    }
    getCustomerList(payload)
      .then((list) => setCustomers(list.map(normalizeCustomerFromApi)))
      .catch(() => setCustomers([]));
  }, [activeTab, debouncedSearchKeyword]);

  const debouncedSetSearchKeyword = useMemo(
    () => debounce((value) => {
      setDebouncedSearchKeyword(value);
    }, 500),
    [],
  );

  const onSearchChange = useCallback((e) => {
    const { value } = e.target;
    setSearchKeyword(value);
    debouncedSetSearchKeyword(value);
    setCurrentPage(1);
  }, [debouncedSetSearchKeyword]);

  useEffect(() => () => {
    debouncedSetSearchKeyword.cancel();
  }, [debouncedSetSearchKeyword]);

  const handleTabChange = useCallback((tab) => () => {
    setActiveTab(tab);
    setCurrentPage(1);
  }, []);

  const onBlockUser = useCallback((user) => () => {
    setBlockingUser(user);
  }, []);

  const onConfirmBlockUser = useCallback(async () => {
    if (!blockingUser) return;
    try {
      await blockCustomer(blockingUser._id);
      showSuccess('Chặn khách hàng thành công');
      setCustomers((prev) =>
        prev.map((item) =>
          (item._id === blockingUser._id ? { ...item, status: 'BLOCKED' } : item),
        ),
      );
      setBlockingUser(null);
    } catch (error) {
      // giữ modal mở, không đóng để user có thể thử lại
    }
  }, [blockingUser, setCustomers]);

  const onCloseConfirmBlockModal = useCallback(() => {
    setBlockingUser(null);
  }, []);

  const onUnblockUser = useCallback((id) => async () => {
    try {
      await unblockCustomer(id);
      showSuccess('Bỏ chặn khách hàng thành công');
      setCustomers((prev) =>
        prev.map((item) =>
          (item._id === id ? { ...item, status: 'ACTIVE' } : item),
        ),
      );
    } catch (error) {
      // API error handled by client/saga/middleware
    }
  }, [setCustomers]);

  const onUpgradeUser = useCallback((user) => () => {
    setUpgradingUser(user);
  }, []);

  const onSubmitUpgradeUser = useCallback((userId, newType) => {
    setCustomers((prev) =>
      prev.map((item) =>
        (item._id === userId ? { ...item, accountType: newType } : item),
      ),
    );
  }, [setCustomers]);

  const onCloseUpgradeModal = useCallback(() => {
    setUpgradingUser(null);
  }, []);

  const onSubmitAddAccount = useCallback((values) => {
    const newCustomer = {
      _id: String(Date.now()),
      fullName: values.fullName,
      email: values.email,
      phoneNumber: values.phoneNumber,
      accountType: 'Du khách',
      lastLogin: 'Vừa xong',
      status: 'ACTIVE',
    };
    setCustomers((prev) => [...prev, newCustomer]);
    onToggleModalAddAccount();
  }, [setCustomers, onToggleModalAddAccount]);

  const filteredCustomers = useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase();
    return customers.filter((item) => {
      // Filter by tab
      if (item.status !== activeTab) return false;

      // Filter by keyword
      if (!keyword) return true;
      return (
        item.fullName.toLowerCase().includes(keyword) ||
        item.email.toLowerCase().includes(keyword) ||
        item.phoneNumber.toLowerCase().includes(keyword)
      );
    });
  }, [customers, activeTab, searchKeyword]);

  const pageCount = Math.ceil(filteredCustomers.length / ITEMS_PER_PAGE);

  const pagedCustomers = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredCustomers.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredCustomers, currentPage]);

  const onChangePage = useCallback(({ selected }) => {
    setCurrentPage(selected + 1);
  }, []);

  // Extract initials matching mockup layout
  const getAvatarInitials = useCallback((name) => {
    if (!name) return 'TP';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      const first = parts[0].charAt(0).toUpperCase();
      const last = parts[parts.length - 1].charAt(0).toUpperCase();
      return `${first}${last}`;
    }
    return name.slice(0, 2).toUpperCase();
  }, []);

  return (
    <div className={`page-content ${styles.Page}`}>
      <Meta name={PAGE_TITLE} />

      <Container fluid>
        <Breadcrumb
          pageTitle="TÀI KHOẢN KHÁCH HÀNG"
          breadcrumbList={breadcrumbList}
        />

        <Card className="Section">
          <div className="Section__Search">
            <div className="Section__Search__Container">
              <Input
                placeholder="Tìm theo tên, số điện thoại, địa chỉ email"
                className="Section__Search__Input"
                value={searchKeyword}
                onChange={onSearchChange}
              />
              <img
                src={SearchIcon}
                alt="search"
                className="Section__Search__Icon__Search"
              />
            </div>

            <div className="Button__Add__Wrapper">
              <Button
                classes="Button__Add"
                fontSize="13px"
                onClick={onOpenModalAddAccount}
              >
                <img src={AddIcon} alt="add" />
                Thêm tài khoản
              </Button>
            </div>
          </div>
        </Card>

        <Card className="Section">
          <div className={styles.TabsContainer}>
            <button
              type="button"
              className={`${styles.TabItem} ${activeTab === TABS.ACTIVE ? styles.TabItemActive : ''}`}
              onClick={handleTabChange(TABS.ACTIVE)}
            >
              Đang hoạt động
            </button>
            <button
              type="button"
              className={`${styles.TabItem} ${activeTab === TABS.BLOCKED ? styles.TabItemActive : ''}`}
              onClick={handleTabChange(TABS.BLOCKED)}
            >
              Đã chặn
            </button>
          </div>

          <div className={styles.TableContainer}>
            <Table responsive className="Table">
              <thead className="Table__Header">
                <tr className="Table__Header__Row">
                  <th className="Table__Header__Row__Content" style={{ minWidth: '220px' }}>Người dùng</th>
                  <th className="Table__Header__Row__Content" style={{ minWidth: '180px' }}>Phân loại tài khoản</th>
                  <th className="Table__Header__Row__Content" style={{ minWidth: '240px' }}>Email</th>
                  <th className="Table__Header__Row__Content" style={{ minWidth: '160px' }}>Số điện thoại</th>
                  <th className="Table__Header__Row__Content" style={{ minWidth: '160px' }}>Lần đăng nhập cuối</th>
                  <th className="Table__Header__Row__Content" style={{ minWidth: '220px' }}>Thao tác</th>
                </tr>
              </thead>

              <tbody className="Table__Body">
                {pagedCustomers.length > 0 ? (
                  pagedCustomers.map((item) => (
                    <tr className="Table__Body__Row" key={item._id}>
                      <td className="Table__Body__Row__Content">
                        <div className={styles.UserInfoCell}>
                          <div className={styles.AvatarWrapper}>
                            {item.avatar && item.avatar.startsWith(S3_AVATAR_PREFIX) ? (
                              <img
                                src={item.avatar}
                                alt={item.fullName}
                                className={styles.AvatarImg}
                              />
                            ) : (
                              getAvatarInitials(item.fullName)
                            )}
                          </div>
                          <span className={styles.UserName}>{item.fullName}</span>
                        </div>
                      </td>
                      <td className="Table__Body__Row__Content">{item.accountType}</td>
                      <td className="Table__Body__Row__Content">{item.email}</td>
                      <td className="Table__Body__Row__Content">{item.phoneNumber}</td>
                      <td className="Table__Body__Row__Content">{item.lastLogin}</td>
                      <td className="Table__Body__Row__Content">
                        <div className={styles.ActionCell}>
                          {item.status === 'ACTIVE' ? (
                            <>
                              <button
                                type="button"
                                className={styles.BtnUpgrade}
                                onClick={onUpgradeUser(item)}
                              >
                                Nâng cấp tài khoản
                              </button>
                              <button
                                type="button"
                                className={styles.BtnBlock}
                                onClick={onBlockUser(item)}
                              >
                                Chặn
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              className={styles.BtnUnblock}
                              onClick={onUnblockUser(item._id)}
                            >
                              Bỏ chặn
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className={styles.NoData}>
                      Không tìm thấy tài khoản nào
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
          {pageCount > 1 && (
            <Pagination
              page={currentPage}
              pageCount={pageCount}
              onChangePage={onChangePage}
            />
          )}
        </Card>

      </Container>

      <ModalAddAccommodationAccount
        isOpen={isOpenModalAddAccount}
        onToggleModal={onToggleModalAddAccount}
        onSubmit={onSubmitAddAccount}
      />

      <ModalAdminConfirm
        isOpen={!!blockingUser}
        onCloseClick={onCloseConfirmBlockModal}
        onAcceptConfirm={onConfirmBlockUser}
        src={images.modalWarning.default}
        notification={{
          message: `Bạn có chắc chắn muốn chặn ${blockingUser?.fullName || ''} không?`,
        }}
        btnConfirmName="Xác nhận"
        btnCloseName="Hủy"
      />

      <ModalUpgradeCustomerAccount
        isOpen={!!upgradingUser}
        onToggleModal={onCloseUpgradeModal}
        user={upgradingUser}
        onSubmit={onSubmitUpgradeUser}
      />
    </div>
  );
}

export default ManagerCustomerAccount;
